import {
  type Airline,
  type BookedFlightItinerary,
  type BookedFlightItineraryWithDepartureTime,
  type FlightItinerarySegment,
  FlightItinerarySegmentStatus,
  type AirBookingFlightItinerarySlice as ApiFlightItinerarySlice,
  type MultiTravelItinerary,
  type Person,
  type SeatedPassengers,
  type SegmentAirlineInfo,
  type SingleTravelItinerary,
  type TravelItinerary,
  TravelItineraryEnum,
} from "@b2bportal/air-booking-api";
import {
  type Segment,
  type Slice,
  type TripSlice,
  UtaCategory,
} from "@b2bportal/air-shopping-api";
import {
  AirRestrictionStatus,
  type AirportMap,
  type DullesUta,
  type FareDetails,
  type FareSegment,
  type FareSliceDetails,
  type FlightItinerarySlice,
  FlightRatingsEnum,
  type IConfirmationNumber,
  type Restriction,
  type TripDetails,
  type TripSegment,
  getDepartureSlice,
  getDurationFromSegments,
  getReturnSlice,
} from "@hopper-b2b/types";
import dayjs from "dayjs";
import type { TFunction } from "i18next";
import { isEqual, uniqWith } from "lodash-es";
import { getCalendarDayDifference, removeTimezone } from "./dayjsHelpers";

export const getSliceIndex = (
  departure: boolean,
  details: TripDetails | FareDetails
): number => {
  return details?.slices.findIndex((slice) =>
    departure ? slice.outgoing : !slice.outgoing
  );
};

export const getFilteredFareDetails = ({
  fareDetails,
  selectedFareClassFilters,
  isOutgoing,
  selectedFareId,
}: {
  fareDetails: FareDetails[];
  selectedFareClassFilters: Array<number>;
  isOutgoing: boolean;
  selectedFareId?: string;
}) => {
  // userSelectedFare, defaults to first fare in list if none selected
  const selectedFare = fareDetails.find((f) => f.id === selectedFareId);
  const sliceIndex = isOutgoing ? 0 : 1;
  const selectedFareRating =
    selectedFare?.slices[sliceIndex]?.fareShelf?.rating || -1;
  return fareDetails.filter((fare: FareDetails) => {
    const fareSlice = fare.slices[sliceIndex];
    const fareSliceRating = getFareSliceRating(fareSlice);
    // Validate that fare slice has fareShelf defined
    if (fareSlice.fareShelf) {
      if (selectedFareClassFilters.length > 0) {
        // Filters applied
        return filterWithFareShelfRatings(
          selectedFareClassFilters,
          fareSliceRating
        );
      } else {
        // No filters applied
        if (selectedFareRating >= 0) {
          // If there's a selectedFare, show higher fares only
          return fareSliceRating >= selectedFareRating;
        }
      }
    }
    return true;
  });
};

export const getSortedFareDetails = (fareDetails: FareDetails[]) =>
  fareDetails.sort((a, b) => {
    const aPrice = a.paxPricings.reduce(
      (sum, p) => sum + (p.pricing?.total?.fiat.value || 0),
      0
    );
    const bPrice = b.paxPricings.reduce(
      (sum, p) => sum + (p.pricing?.total?.fiat.value || 0),
      0
    );
    if (aPrice) {
      if (bPrice) {
        if (aPrice < bPrice) {
          return -1;
        } else if (aPrice > bPrice) {
          return 1;
        }
      }
    }
    return 0;
  });

const filterWithFareShelfRatings = (
  selectedFareClassFilters: Array<number>,
  fareSliceRating: number
) => {
  const maxShelfRating = Math.max(...selectedFareClassFilters);

  return (
    selectedFareClassFilters.includes(fareSliceRating) ||
    fareSliceRating >= maxShelfRating
  );
};

const getFareSliceRating = (fareSlice: FareSliceDetails) =>
  typeof fareSlice?.fareShelf?.rating === "number" &&
  fareSlice.fareShelf.rating >= 0
    ? fareSlice.fareShelf.rating
    : -1;

export const getAirlineInfoFromBookedFlightItinerary = (
  flight: BookedFlightItineraryWithDepartureTime | null
): Array<SegmentAirlineInfo> => {
  if (!flight) {
    return [];
  }
  const { bookedItinerary } = flight;
  const { travelItinerary } = bookedItinerary;
  const { TravelItinerary } = travelItinerary;

  if (TravelItinerary === TravelItineraryEnum.SingleTravelItinerary) {
    const singleTravelItinerary = travelItinerary as SingleTravelItinerary;
    return singleTravelItinerary.slices.reduce(
      (locators, slice) => [
        ...locators,
        ...slice.segments.map((segment) => segment.marketingAirline),
      ],
      [] as Array<SegmentAirlineInfo>
    );
  } else if (TravelItinerary === TravelItineraryEnum.MultiTravelItinerary) {
    const multipleTravelItinerary = travelItinerary as MultiTravelItinerary;

    if (multipleTravelItinerary.locators?.children) {
      return multipleTravelItinerary.travelItineraries.reduce(
        (locators, itinerary) => [
          ...locators,
          ...itinerary.slices
            .map((slice) =>
              slice.segments.map((segment) => segment.marketingAirline)
            )
            .flat(),
        ],
        [] as Array<SegmentAirlineInfo>
      );
    }
  }
  return [];
};

export const getAirlineLocatorsFromBookedFlightItinerary = (
  flight: BookedFlightItineraryWithDepartureTime | null
): string => {
  const airlineInfo = getAirlineInfoFromBookedFlightItinerary(flight);
  const locators = Array.from(airlineInfo).map(
    (info) => `${info.code} - ${info.locator}`
  );
  return uniqWith(locators, isEqual).join(", ");
};

export const getHopperLocatorFromBookedFlightItinerary = (
  flight: BookedFlightItineraryWithDepartureTime | null
) =>
  (flight?.bookedItinerary?.travelItinerary as SingleTravelItinerary)?.locators
    ?.agent?.unscopedValue || "";

/**
 * Resolver to get display name for an airport/location code (e.g. from AirportMap).
 */
export type AirportDisplayNameResolver = (locationCode: string) => string;

/**
 * Returns origin and destination codes and display names for a flight slice
 * (first segment origin, last segment destination). Use with getFlightItineraryLegs
 * for consistent single/multi-city handling.
 */
export const getOriginDestinationFromSlice = (
  slice: {
    segments?: Array<{
      origin?: { locationCode?: string };
      destination?: { locationCode?: string };
    }>;
  },
  getDisplayName: AirportDisplayNameResolver
): {
  originCode: string;
  destinationCode: string;
  originName: string;
  destinationName: string;
} => {
  const segments = slice.segments ?? [];
  const first = segments[0];
  const last = segments[segments.length - 1];
  const originCode = first?.origin?.locationCode ?? "";
  const destinationCode = last?.destination?.locationCode ?? "";
  return {
    originCode,
    destinationCode,
    originName: getDisplayName(originCode),
    destinationName: getDisplayName(destinationCode),
  };
};

/**
 * Returns one leg per segment of the trip for display (outbound only for single/round-trip,
 * one per travelItinerary for multi-city). Use with getOriginDestinationFromSlice to build
 * titles/labels without duplicating single vs multi-city logic.
 */
export const getFlightItineraryLegs = (
  itinerary: BookedFlightItineraryWithDepartureTime
): Array<{ slice: ApiFlightItinerarySlice }> => {
  const travelItinerary = itinerary.bookedItinerary?.travelItinerary;
  if (!travelItinerary) return [];
  const { TravelItinerary } = travelItinerary;

  if (TravelItinerary === TravelItineraryEnum.SingleTravelItinerary) {
    const single = travelItinerary as SingleTravelItinerary;
    const slices = single.slices ?? [];
    const outbound = slices[0];
    return outbound != null ? [{ slice: outbound }] : [];
  }

  if (TravelItinerary === TravelItineraryEnum.MultiTravelItinerary) {
    const multi = travelItinerary as MultiTravelItinerary;
    const travelItineraries = multi.travelItineraries ?? [];
    return travelItineraries
      .map((ti) => ti.slices?.[0])
      .filter((s): s is ApiFlightItinerarySlice => s != null)
      .map((slice) => ({ slice }));
  }

  return [];
};

export const getEmptyRestrictionsText = (
  fareRating: number | undefined,
  translate: TFunction
) => {
  switch (fareRating) {
    case FlightRatingsEnum.basic:
      return translate("fareClass.basicDetails");
    case FlightRatingsEnum.standard:
      return translate("fareClass.standardDetails");
    case FlightRatingsEnum.enhanced:
      return translate("fareClass.enhancedDetails");
    case FlightRatingsEnum.premium:
      return translate("fareClass.premiumDetails");
    case FlightRatingsEnum.luxury:
      return translate("fareClass.luxuryDetails");
    default:
      return "";
  }
};

export const layoverDetails = ({
  tripDetails,
  airports,
  isOutgoing,
}: {
  airports: AirportMap;
  tripDetails: TripDetails;
  isOutgoing: boolean;
}) => {
  return tripDetails.slices
    .filter((slice) => isOutgoing === slice.outgoing)
    .map((slice) =>
      slice.segmentDetails.map(
        ({ airlineCode, originCode, destinationCode, airlineName }) => ({
          airlineCode,
          originCode,
          destinationCode,
          airlineName,
          originCity: airports?.[originCode]?.cityName,
          destinationCity: airports?.[destinationCode]?.cityName,
        })
      )
    )
    .flat();
};

export const getRestrictions = ({
  fareDetails,
  sliceIndex,
  translate,
  customerSupport,
  cfarSelected = false,
  chfarSelected = false,
}: {
  fareDetails: FareDetails;
  sliceIndex: number;
  translate: TFunction;
  customerSupport: boolean;
  cfarSelected: boolean | undefined;
  chfarSelected: boolean | undefined;
}): Restriction[] => {
  const restrictions: Restriction[] =
    fareDetails.slices[sliceIndex].amenitiesUtas?.utas.utas.map(
      (uta: DullesUta) => {
        const cfarIncluded =
          uta.category === UtaCategory.cancellation && cfarSelected;
        const chfarIncluded =
          uta.category === UtaCategory.advance_change && chfarSelected;

        const restriction: Restriction = {
          // TODO: the assessment field should probably be an enum
          symbol:
            uta.assessment === "benefit" || cfarIncluded || chfarIncluded
              ? AirRestrictionStatus.INCLUDED
              : uta.assessment === "fee"
                ? AirRestrictionStatus.PURCHASABLE
                : AirRestrictionStatus.UNAVAILABLE,
          name: translate(`amenityUtaCategories.${uta.category}`),
          description: cfarIncluded
            ? translate("cfarOffers.restrictionsCfarText")
            : chfarIncluded
              ? translate("chOffer.restrictionsChfarText")
              : uta.description,
        };

        return restriction;
      }
    ) ?? [];

  if (customerSupport) {
    restrictions.push({
      symbol: AirRestrictionStatus.INCLUDED,
      name: translate("flightShopReview.customerSupportTitle"),
      description: translate("flightShopReview.customerSupportSubtitle"),
    });
  }
  return restrictions;
};

export const getPlusDays = (trip: TripSlice): number =>
  trip?.segmentDetails
    .filter((s) => s.plusDays && s.plusDays > 0)
    .reduce((total, segment) => total + (segment.plusDays ?? 0), 0);

// for individual pieces of a tripSlice
export const getPlusDaysOnSlice = (slice: Slice): number => {
  return slice?.segments
    .filter((segment) => segment.plusDays && segment.plusDays > 0)
    .reduce((total, segment) => total + (segment.plusDays ?? 0), 0);
};

export const getPlusDaysOnSliceWithDates = (
  departureDate: string,
  arrivalDate: string
) => {
  const departure = removeTimezone(departureDate);
  const arrival = removeTimezone(arrivalDate);
  return getCalendarDayDifference(departure, arrival);
};

// mixed cabin is true if the following conditions are met:
// - fareShelf rating of 3 or above AND one of the below:
//  - the shortBrandName is PREMIUM ECONOMY AND one of the segment has a cabinClassName of ECONOMY
//  - the shortBrandName is FIRST or BUSINESS (not premium economy) AND one of the segment includes the word "Economy" (PREMIUM ECONOMY/ECONOMY)
// note: checking using the shortBrandName instead of the cabinClassName because the cabinClassName mapping is currently incorrect in same cases (we were getting back a cabinClassName of FIRST instead of PREMIUM ECONOMY)
export const getIsMixedClass = (
  slice: FareSliceDetails | FlightItinerarySlice | ApiFlightItinerarySlice
) => {
  if (!slice?.fareShelf || slice?.fareShelf?.rating >= 3) {
    let segments: FareSegment[] | FlightItinerarySegment[];

    if ("fareDetails" in slice) {
      ({ segments } = slice.fareDetails);
    } else {
      ({ segments } = slice);
    }

    if (segments.length <= 1) return false;

    const lookupCabinClass = segments[0].cabinClassName;

    if (lookupCabinClass && lookupCabinClass.length === 1) {
      // unioned segments type needs to split up to fix a TS error
      if (isFareSegmentArray(segments)) {
        return segments.every(
          (segment) => segment.cabinClassName === lookupCabinClass
        );
      }

      return segments.every(
        (segment) => segment.cabinClassName === lookupCabinClass
      );
    }

    return segments.some((segment: FareSegment | FlightItinerarySegment) => {
      const { brandName, shortBrandName } = slice.fareShelf ?? {};
      const { cabinClassName } = segment;

      if (
        brandName != null &&
        brandName.includes("First") &&
        shortBrandName === "Premium"
      ) {
        return cabinClassName === "Economy";
      }

      return cabinClassName?.includes("Economy") ?? false;
    });
  }

  return false;
};

export function getSliceDuration(slice: FlightItinerarySlice) {
  const duration = getDurationFromSegments(
    slice.segments,
    (departure, arrival) => dayjs(arrival).diff(dayjs(departure), "m")
  );
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;

  return dayjs.duration({ hours, minutes }).format("H[h] m[m]");
}

/**
 * @description Gets the originally scheduled slice times to be striked through
 */
export function getSkchSliceTime(slice: FlightItinerarySlice) {
  const firstSeg = slice.segments[0];
  const lastSeg = slice.segments[slice.segments.length - 1];
  const departure = dayjs(removeTimezone(firstSeg.scheduledDeparture));
  const arrival = dayjs(removeTimezone(lastSeg.scheduledArrival));

  return `${departure.format("h:mm A")} - ${arrival.format("h:mm A")}`;
}

export function getSliceTime(slice: FlightItinerarySlice) {
  const firstSeg = slice.segments[0];
  const lastSeg = slice.segments[slice.segments.length - 1];
  const dep =
    firstSeg.zonedUpdatedDeparture ||
    firstSeg.updatedDeparture ||
    firstSeg.zonedScheduledDeparture ||
    firstSeg.scheduledDeparture;
  const ret =
    lastSeg.zonedUpdatedArrival ||
    lastSeg.updatedArrival ||
    lastSeg.zonedScheduledArrival ||
    lastSeg.scheduledArrival;
  const departure = dayjs(removeTimezone(dep));
  const returnTime = dayjs(removeTimezone(ret));

  return `${departure.format("h:mm A")} - ${returnTime.format("h:mm A")}`;
}

export function isFareSegmentArray(
  segments: FareSegment[] | FlightItinerarySegment[]
): segments is FareSegment[] {
  return "fareClass" in segments[0];
}

export const getAirlinesCountSegment = (segment: Segment[] | TripSegment[]) => {
  const set = new Set(
    (segment ?? []).map(
      (segment) => segment.marketingAirline.code || segment.marketingAirline
    )
  );
  return set.size - 1;
};

export const getVirtualInterlineLayovers = (
  fareDetails: FareDetails,
  outbound: boolean
) => {
  return fareDetails.slices[outbound ? 0 : 1]?.fareDetails.segments.filter(
    (s) => s.isSelfTransferLayover
  ).length;
};

export interface IConfirmationModalLabels {
  confirmationCode: string;
  return: string;
  outbound: string;
  originToDestination: (origin: string, destination: string) => string;
}

export interface IGetConfirmationNumberProps {
  segment: FlightItinerarySegment;
  airlineMap: {
    [key: string]: Airline | undefined;
  };
  isReturn?: boolean;
  labels?: IConfirmationModalLabels;
}

export const getConfirmationNumber = ({
  segment,
  airlineMap,
  isReturn,
  labels,
}: IGetConfirmationNumberProps): IConfirmationNumber | false => {
  const selectedAirline = airlineMap[segment.marketingAirline.code];
  if (!selectedAirline) return false;
  const returnLabel = labels?.return;
  const outboundLabel = labels?.outbound;
  const originToDestinationLabel = labels?.originToDestination;

  const label =
    returnLabel && outboundLabel && originToDestinationLabel
      ? `${selectedAirline.displayName} ${
          isReturn ? returnLabel : outboundLabel
        }: ${
          originToDestinationLabel &&
          originToDestinationLabel(
            segment.origin.locationCode,
            segment.destination.locationCode
          )
        }`
      : selectedAirline.displayName;

  return {
    label: label,
    locator: segment.marketingAirline.locator || "",
    redirectUrl: selectedAirline.webLinks.manageBooking || "",
  };
};

/** Type to track segment info for building route labels */
export interface SegmentLocatorInfo {
  locator: string;
  airlineName: string;
  airlineCode: string;
  origin: string;
  destination: string;
  redirectUrl: string;
  isReturn: boolean;
}

/**
 * Builds the full route string from segments (e.g., "DFW → PHX → SEA")
 */
export const buildRouteString = (segments: SegmentLocatorInfo[]): string => {
  if (segments.length === 0) return "";
  if (segments.length === 1) {
    return `${segments[0].origin} → ${segments[0].destination}`;
  }

  // Build route: origin of first segment, then all destinations
  const route = [segments[0].origin];
  segments.forEach((seg) => {
    route.push(seg.destination);
  });
  return route.join(" → ");
};

/**
 * Checks if all segments in a group have the same airline.
 */
export const allSegmentsSameAirline = (
  segments: SegmentLocatorInfo[]
): boolean => {
  if (segments.length <= 1) return true;
  const firstAirlineCode = segments[0].airlineCode;
  return segments.every((seg) => seg.airlineCode === firstAirlineCode);
};

/**
 * Groups segments by locator and builds confirmation numbers with appropriate route labels.
 * - When all segments with the same locator are from the SAME airline: show full route (e.g., "DFW → PHX → SEA")
 * - When segments with the same locator are from DIFFERENT airlines: only show the validating carrier's segment
 *   (This handles interline/codeshare cases where one locator is incorrectly duplicated across carriers)
 */
export const buildConfirmationNumbersFromSegments = (
  segmentInfos: SegmentLocatorInfo[],
  labels?: IConfirmationModalLabels
): IConfirmationNumber[] => {
  // Group segments by locator
  const groupedByLocator: Record<string, SegmentLocatorInfo[]> = {};
  segmentInfos.forEach((info) => {
    if (!groupedByLocator[info.locator]) {
      groupedByLocator[info.locator] = [];
    }
    groupedByLocator[info.locator].push(info);
  });

  return Object.entries(groupedByLocator).map(([locator, segments]) => {
    const firstSegment = segments[0];
    const directionLabel = firstSegment.isReturn
      ? labels?.return || "Return"
      : labels?.outbound || "Outbound";

    // Only show full route if all segments are from the same airline
    // For interline bookings with different carriers sharing the same locator,
    // only show the validating carrier's (first) segment
    const segmentsToShow = allSegmentsSameAirline(segments)
      ? segments
      : [firstSegment];
    const routeString = buildRouteString(segmentsToShow);

    return {
      label: `${firstSegment.airlineName} ${directionLabel}: ${routeString}`,
      locator,
      redirectUrl: firstSegment.redirectUrl,
    };
  });
};

/**
 * Collects segment info with locator data for building route labels.
 */
export const collectSegmentLocatorInfo = ({
  segments,
  airlineMap,
  isReturn,
}: {
  segments: FlightItinerarySegment[];
  airlineMap: { [key: string]: Airline | undefined };
  isReturn: boolean;
}): SegmentLocatorInfo[] => {
  const infos: SegmentLocatorInfo[] = [];

  segments.forEach((segment) => {
    const locator = segment.marketingAirline.locator;
    if (!locator) return;

    const airline = airlineMap[segment.marketingAirline.code];
    if (!airline) return;

    infos.push({
      locator,
      airlineName: airline.displayName,
      airlineCode: segment.marketingAirline.code,
      origin: segment.origin.locationCode,
      destination: segment.destination.locationCode,
      redirectUrl: airline.webLinks.manageBooking || "",
      isReturn,
    });
  });

  return infos;
};

/**
 * Collects segment infos from a flight itinerary for building confirmation numbers.
 */
const collectAllSegmentInfos = ({
  flight,
  airlineMap,
}: {
  flight: BookedFlightItineraryWithDepartureTime;
  airlineMap: { [key: string]: Airline | undefined };
}): SegmentLocatorInfo[] => {
  const { bookedItinerary } = flight;
  const { travelItinerary } = bookedItinerary;
  const { TravelItinerary } = travelItinerary;

  const allSegmentInfos: SegmentLocatorInfo[] = [];

  if (TravelItinerary === TravelItineraryEnum.SingleTravelItinerary) {
    const singleTravelItinerary = travelItinerary as SingleTravelItinerary;
    singleTravelItinerary.slices.forEach((slice, sliceIndex) => {
      const segmentInfos = collectSegmentLocatorInfo({
        segments: slice.segments,
        airlineMap,
        isReturn: sliceIndex !== 0,
      });
      allSegmentInfos.push(...segmentInfos);
    });
  }
  if (TravelItinerary === TravelItineraryEnum.MultiTravelItinerary) {
    const multipleTravelItinerary = travelItinerary as MultiTravelItinerary;

    if (multipleTravelItinerary.locators?.children) {
      multipleTravelItinerary.travelItineraries.forEach((itinerary) => {
        itinerary.slices.forEach((slice, sliceIndex) => {
          const segmentInfos = collectSegmentLocatorInfo({
            segments: slice.segments,
            airlineMap,
            isReturn: sliceIndex !== 0,
          });
          allSegmentInfos.push(...segmentInfos);
        });
      });
    }
  }

  return allSegmentInfos;
};

/**
 * Gets airline-only confirmation numbers from a flight itinerary (without tenant locator).
 * Used for check-in modal where only airline locators are needed.
 */
export const getAirlineConfirmationNumbers = ({
  flight,
  airlineMap,
  labels,
}: {
  flight: BookedFlightItineraryWithDepartureTime;
  airlineMap: { [key: string]: Airline | undefined };
  labels?: IConfirmationModalLabels;
}): IConfirmationNumber[] => {
  const allSegmentInfos = collectAllSegmentInfos({ flight, airlineMap });
  return buildConfirmationNumbersFromSegments(allSegmentInfos, labels);
};

/**
 * Gets confirmation numbers from a flight itinerary, grouped by unique locator values.
 * Includes tenant/agent locator plus airline locators.
 * This prevents showing the same locator multiple times for different carriers in interline/codeshare bookings.
 * When multiple segments share the same locator, the label shows the full route (e.g., "DFW → PHX → SEA").
 */
export const getConfirmationNumbers = ({
  flight,
  airlineMap,
  labels,
}: {
  flight: BookedFlightItineraryWithDepartureTime;
  airlineMap: { [key: string]: Airline | undefined };
  labels?: IConfirmationModalLabels;
}): IConfirmationNumber[] => {
  const { bookedItinerary } = flight;
  const { travelItinerary } = bookedItinerary;

  const confirmationNumbers: IConfirmationNumber[] = [];

  // Add tenant/agent locator if present
  if (
    (travelItinerary as SingleTravelItinerary)?.locators?.agent?.unscopedValue
  ) {
    confirmationNumbers.push({
      label: labels ? labels.confirmationCode : "Hopper Travel",
      locator:
        (travelItinerary as SingleTravelItinerary).locators?.agent
          ?.unscopedValue || "",
      redirectUrl: "https://www.hopper.com/",
      unscopedValue:
        (travelItinerary as SingleTravelItinerary).locators?.agent
          ?.unscopedValue || "",
    });
  }

  // Add airline locators
  const airlineLocators = getAirlineConfirmationNumbers({
    flight,
    airlineMap,
    labels,
  });
  confirmationNumbers.push(...airlineLocators);

  return confirmationNumbers;
};

export const getLocatorsWithLabelFromItinerary = ({
  itinerary,
  airlineMap,
}: {
  itinerary: TravelItinerary;
  airlineMap: { [key: string]: Airline | undefined };
}): Array<{ locator: string; label: string }> => {
  const { TravelItinerary } = itinerary;

  const confirmationNumbers: Array<{ locator: string; label: string }> = [];

  if (TravelItinerary === TravelItineraryEnum.SingleTravelItinerary) {
    const singleTravelItinerary = itinerary as SingleTravelItinerary;
    singleTravelItinerary.slices.forEach((slice) => {
      slice.segments.forEach((segment) => {
        const confirmationNumber = getLocatorWithLabelFromSegment({
          segment,
          airlineMap,
        });
        if (confirmationNumber) {
          confirmationNumbers.push(confirmationNumber);
        }
      });
    });
  }
  if (TravelItinerary === TravelItineraryEnum.MultiTravelItinerary) {
    const multipleTravelItinerary = itinerary as MultiTravelItinerary;

    if (multipleTravelItinerary.locators?.children) {
      multipleTravelItinerary.travelItineraries.forEach((itinerary) => {
        itinerary.slices.forEach((slice) => {
          slice.segments.forEach((segment) => {
            const confirmationNumber = getLocatorWithLabelFromSegment({
              segment,
              airlineMap,
            });
            if (confirmationNumber) {
              confirmationNumbers.push(confirmationNumber);
            }
          });
        });
      });
    }
  }
  return uniqWith(confirmationNumbers, (a, b) => a.locator === b.locator);
};

export const getLocatorWithLabelFromSegment = ({
  segment,
  airlineMap,
}: {
  segment: FlightItinerarySegment;
  airlineMap: { [key: string]: Airline | undefined };
}): { locator: string; label: string } | false => {
  const selectedAirline = airlineMap[segment.marketingAirline.code];
  if (!selectedAirline) return false;

  return {
    label: selectedAirline.displayName,
    locator: segment.marketingAirline.locator || "",
  };
};

export const getPassengerMap = (passengers: SeatedPassengers) => {
  const { alone, withLapInfants } = passengers;
  const passengerMap: Record<string, Person> = {};

  alone.reduce((map, p) => {
    if (!map[p.person.id]) {
      map[p.person.id] = p.person;
    }

    return map;
  }, passengerMap);

  withLapInfants.reduce((map, p) => {
    if (!map[p.adult.person.id]) {
      map[p.adult.person.id] = p.adult.person;
    }

    return map;
  }, passengerMap);

  return passengerMap;
};

export const sliceHasScheduleChange = (
  bookedItinerary: BookedFlightItinerary,
  isOutgoing = false
) => {
  const slice = isOutgoing
    ? getDepartureSlice(bookedItinerary)
    : getReturnSlice(bookedItinerary);

  if (slice) {
    return slice.segments.some(
      (s) =>
        s.status === FlightItinerarySegmentStatus.ConfirmedPendingNewChange ||
        s.status === FlightItinerarySegmentStatus.UnMapped ||
        s.status === FlightItinerarySegmentStatus.UnMappedPersisted
    );
  }

  return false;
};
